import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import _ from "lodash";

import { connect } from "react-redux";
import PActions from "../../Stores/redux/Persisted/Actions";
import UnpActions from "../../Stores/redux/Unpersisted/Actions";

import Modal from "../../Components/Modal/Modal";
import AutoCompleteDropdownStatic from "../../Components/Input/AutoCompleteDropdownStatic";
import api from "../../Services/Api/api";
import etcModule from "../../Modules/etc/etc-module";

const NewOfficeForm = ({
  isOpen,
  onClose,
  locationData,
  setScreenState,
  user,
  onSuccess,
  officeCategories,
  officeTypes,
}) => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [subdivisionOptions, setSubdivisionOptions] = useState(null);
  const [blockOptions, setBlockOptions] = useState(null);
  const [deptBlockOptions, setDeptBlockOptions] = useState(null);
  const [circleOptions, setCircleOptions] = useState(null);
  const [lacOptions, setLacOptions] = useState(null);
  const [officeOptions, setOfficeOptions] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    category: null,
    type: null,
    district: user.district._id,
    subdivision: null,
    block: null,
    deptBlock: null,
    circle: null,
    lac: null,
    details: "",
  });

  const totalSteps = 2;

  useEffect(() => {
    if (isOpen) {
      if (!locationData) {
        loadLocationData();
      }
      if (!officeCategories || !officeTypes) {
        loadOfficeTypes();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (locationData) {
      setSubdivisionOptions([
        {
          items: locationData?.subdivisions?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setBlockOptions([
        {
          items: locationData?.blocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setDeptBlockOptions([
        {
          items: locationData?.deptBlocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setCircleOptions([
        {
          items: locationData?.circles?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setLacOptions([
        {
          items: locationData?.lacs?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
    }
  }, [locationData]);

  useEffect(() => {
    if (formData.type) {
      const officeType = officeTypes.find((x) => x._id === formData.type);
      if (officeType.category !== formData.category) {
        handleChange("type", null);
      }
    }
  }, [formData.category]);

  const loadOfficeTypes = async () => {
    try {
      const { officeTypes, officeCategories } = await api.get(
        "v1/appdata/office-data"
      );
      setScreenState({ officeTypes, officeCategories }, false, "APP");
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  const loadLocationData = async () => {
    try {
      const locationData = await api.get("v1/appdata/location-data", {
        district: user.district._id,
      });
      setScreenState(
        { locationData: { ...locationData, uid: Date.now() } },
        false,
        "APP"
      );
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  // Handle Next / Previous Steps
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  // Handle Input Change
  const handleChange = (name, value) =>
    setFormData({ ...formData, [name]: value });

  // Submit Form
  const handleSubmit = async () => {
    try {
      if (loading) return;

      setError(null);
      setLoading(true);

      const { office } = await api.post("v1/district-admin/office", formData);
      console.log({ office });
      toast.success("Office created successfully");

      setLoading(false);
      onClose(); // Close modal after submit
      if (onSuccess) onSuccess(office);
    } catch (e) {
      setError(e.message);
      setLoading(false);
      toast.error("Error: " + e.message);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Office"
      showFooter={false}
      closeOnOutsideClick={false}
    >
      {/* Step Indicator */}
      <div className="step-indicator">
        Step {step} of {totalSteps}
      </div>

      {/* Step 1 - Location Selection */}
      {step === 1 && (
        <>
          <div className="form-step">
            <label>District</label>
            <input type="text" value={user.district.name} disabled />

            <label>Subdivision</label>
            <AutoCompleteDropdownStatic
              options={subdivisionOptions}
              placeholder="Select Subdivision"
              value={formData.subdivision}
              onSelect={(value) => handleChange("subdivision", value)}
              loading={!subdivisionOptions}
            />

            <label>Block</label>
            <AutoCompleteDropdownStatic
              options={blockOptions}
              placeholder="Select Block"
              value={formData.block}
              onSelect={(value) => handleChange("block", value)}
              loading={!blockOptions}
            />

            <label>Circle</label>
            <AutoCompleteDropdownStatic
              options={circleOptions}
              placeholder="Select Circle"
              value={formData.circle}
              onSelect={(value) => handleChange("circle", value)}
              loading={!circleOptions}
            />

            <label>LAC</label>
            <AutoCompleteDropdownStatic
              options={lacOptions}
              placeholder="Select LAC"
              value={formData.lac}
              onSelect={(value) => handleChange("lac", value)}
              loading={!lacOptions}
            />

            <label>Dept Block</label>
            <AutoCompleteDropdownStatic
              options={deptBlockOptions}
              placeholder="Select Block"
              value={formData.deptBlock}
              onSelect={(value) => handleChange("deptBlock", value)}
              loading={!deptBlockOptions}
            />
          </div>

          <div className="modal-footer">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            {formData.district &&
              formData.subdivision &&
              formData.block &&
              formData.circle &&
              formData.lac &&
              formData.deptBlock && (
                <button className="next-btn" onClick={nextStep}>
                  Next
                </button>
              )}
          </div>
        </>
      )}

      {/* Step 2 - Office Details */}
      {step === 2 && (
        <>
          <div className="form-step">
            <label>Office Category</label>
            <AutoCompleteDropdownStatic
              options={[
                {
                  items: officeCategories?.map((x) => ({
                    value: x._id,
                    label: x.name,
                  })),
                },
              ]}
              loading={!officeCategories}
              placeholder="Select Office Category"
              value={formData.category || ""}
              onSelect={(value) => handleChange("category", value)}
            />

            <label>Office Type</label>
            <AutoCompleteDropdownStatic
              options={[
                {
                  items: officeTypes
                    ?.filter((x) => x.category === formData.category)
                    ?.map((x) => ({
                      value: x._id,
                      label: x.name,
                    })),
                },
              ]}
              loading={!officeTypes}
              placeholder="Select Office Type"
              value={formData.type || ""}
              onSelect={(value) => handleChange("type", value)}
            />

            <label>Office Name</label>
            <input
              type="text"
              placeholder="Enter Office Name"
              value={formData.name || ""}
              onChange={(e) => handleChange("name", e.target.value)}
            />

            <label>Details</label>
            <textarea
              placeholder="Enter Office Details"
              value={formData.details || ""}
              onChange={(e) => handleChange("details", e.target.value)}
            ></textarea>
          </div>

          <div className="modal-footer">
            <button className="prev-btn" onClick={prevStep}>
              Previous
            </button>
            {formData.name && (
              <button className="submit-btn" onClick={handleSubmit}>
                {loading ? "Loading.." : "Submit"}
              </button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  locationData: state.vState.APP.locationData,
  officeTypes: state.vState.APP.officeTypes,
  officeCategories: state.vState.APP.officeCategories,
  user: state.pState.AUTH.user,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = "OFFICES_SCREEN") =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewOfficeForm);
